import { Highcharts } from "extensions/highcharts";
import { _ } from "extensions/underscore";

import { toHex } from "common/colors";
import { number } from "common/formats";
import { accounting } from "common/utils";
import strings from "common/strings";
import { v2 } from "common/config/api";
    
import { DefaultTypeConfig } from "./default";

export const authorizations = function(options) {
    const opts = {
        by: "count",
        color: toHex("blue-light"),
        declines: false, // Use `true` for decline_rate
        ...options
    };
    
    let widgetValueData;
    
    if (opts.declines) {
        widgetValueData = {
            widgetValueTemplate: `<div class="rate">{{ data.declineRate}}</div>
                                      <div class="attempts">
                                        <div>${strings.dashboard.widgets.decline_rate.after}</div>
                                        <div class="total-attempts-value">{{ data.totalAttempts }}</div>
                                        <div>${strings.dashboard.widgets.decline_rate.attempts}</div>
                                      </div>`,
            widgetValueTemplateData: {
                declineRate: {
                    value: _.partial(accounting.authorizations.declineRate, _, {
                        by: opts.by,
                        humanize: true,
                        addFloatValue: false
                    })
                },
                totalAttempts: {
                    value: accounting.authorizations.authCount,
                    type: "number"
                }
            },
            widgetNote: `by ${opts.by}`,
            defaultMetric: opts.by
        };
    } else {
        widgetValueData = {
            summaryTemplate: "summary2",
            widgetValueTemplateData: {
                approvalRate: {
                    headerTemplateKey: "calculatedValue",
                    activeValue: true,
                    value(point) {
                        return accounting.authorizations.approvalRate(point, {
                            by: opts.by,
                            humanize: true,
                            addFloatValue: false
                        });
                    },
                    label: strings.dashboard.widgets.approval_rate.self
                },
                totalApprovalsCount: {
                    headerTemplateKey: "val1",
                    activeValue: opts.by === "count",
                    value: accounting.authorizations.approvalsCount,
                    type: "number",
                    label: strings.dashboard.widgets.approval_rate.approvals
                },
                totalAttempts: {
                    headerTemplateKey: "val2",
                    activeValue: opts.by === "count",
                    value: accounting.authorizations.authCount,
                    type: "number",
                    label: strings.dashboard.widgets.approval_rate.attempts
                },
                totalAmountApproved: {
                    headerTemplateKey: "val1",
                    activeValue: opts.by !== "count",
                    value: accounting.authorizations.approvalsAmount,
                    label: strings.dashboard.widgets.approval_rate.approvals,
                    type: "amount"

                },
                totalAmountAttempted: {
                    headerTemplateKey: "val2",
                    activeValue: opts.by !== "count",
                    value: accounting.authorizations.authAmount,
                    label: strings.dashboard.widgets.approval_rate.attempts,
                    type: "amount"
        
                }
            },
            widgetNote: `by ${opts.by}`,
            defaultMetric: opts.by
        };
    }
    
    let totals;
    if (opts.by === "count") {
        totals = {
            declines_count: accounting.authorizations.declinesCount,
            approvals_count: accounting.authorizations.approvalsCount
        };
    } else if (opts.by === "amount") {
        totals = {
            declines_amount: accounting.authorizations.declinesAmount,
            approvals_amount: accounting.authorizations.approvalsAmount
        };
    }
    
    return new DefaultTypeConfig(opts, {
        urlRoot() {
            return "analytics/authorizations";
        },
        api_endpoint(pivot = "date_time") {
            return v2.authorizations.byPivot(pivot, opts.by === "count" ? {
                counts_only: true
            } : void 0);
        },
        totals,
        quick: {
            editable: true,
            refetchOnEdit: () => opts.by === "amount",
            chart: {
                type: "AreaLine",
                series: [{
                    type: "area",
                    pointFormat: [DefaultTypeConfig.date_unix, DefaultTypeConfig.keyToFunc["approval_rate"](_.pick(opts, "by"))],
                    extend: {
                        fillColor: Highcharts.Color(toHex("canvas-white")).setOpacity(0.15).get("rgba"),
                        color: toHex("canvas-white")
                    }
                }]
            },
            data: widgetValueData,
            reactProps: {
                chartField: "approval_rate",
                chartFieldToggle: true,
                chartFieldDataType: "rate",
                secondaryField: "auths",
                secondaryFieldToggle: true,
                secondaryFieldDataType: "by",
                // eslint-disable-next-line sentence-case/sentence-case
                summaryLabel: "of ALL ATTEMPTS",
                fillColor: Highcharts.Color(toHex("canvas-white")).setOpacity(0.15).get("rgba"),
                color: toHex("canvas-white")
            }
        },
        table: {
            columns: [{
                id: "Actor", // This field is changed by tableView.js
                header: "",
                template(model, key) {
                    return DefaultTypeConfig.references.toAlias(model[key], key);
                },
                sort: "string"
            }, {
                id: strings.dashboard.rate,
                header: strings.dashboard.rate,
                template: (model) => accounting.authorizations.approvalRate(model, {
                    by: opts.by,
                    humanize: true
                }),
                css: "value",
                sort: (a, b) => (accounting.authorizations.approvalRate(a) < accounting.authorizations.approvalRate(b) ? -1 : 1)
            }, {
                id: "auths_count",
                header: strings.dashboard.widgets.approval_rate.attempts,
                format: number,
                css: "value",
                initialSorting: ["auths_count", "desc", "int"],
                sort: "int"
            }]
        },
        pie: {
            pointValue: `${opts.declines ? "declines" : "authorizations"}_${opts.by}`,
            pointType: opts.by
        },
        doublePivot: {
            by: opts.by,
            refetchOnEdit: () => opts.by === "amount",
            dataGetter: _.partial(accounting.authorizations.declinesValue, _, {
                by: opts.by
            }),
            showBadgeInTabNavi: false,
            bodyType: opts.bodyType || "chart",
            tooltip: {
                template: (data) => `<div class="doublePivot-tooltip"><b>${data.network} chargeback code ${data.value}:</b><p>${data.desc}</p></div>`,
                data(o) {
                    return {
                        provider: o.primaryPivotAlias,
                        value: o.value,
                        desc: DefaultTypeConfig.references.toAlias(o.value, o.secondaryPivotKey)
                    };
                }
            },
            summary: {
                enabled: true,
                totals({ collection }) {
                    const data = collection.toJSON();
                    
                    const declinesTotal = accounting.authorizations.declinesValue(data, {
                        by: opts.by
                    });
                    
                    const allAttempts = accounting.authorizations.authorizationsValue(data, {
                        by: opts.by
                    });
                    
                    return [{
                        name: strings.allDeclines,
                        value: declinesTotal,
                        valueType: opts.by
                    }, {
                        name: strings.declineRate,
                        value: accounting.getRate({
                            value: declinesTotal,
                            total: allAttempts,
                            precision: 2,
                            humanize: true,
                            addFloatValue: false
                        }),
                        valueType: "rate"
                    }, {
                        name: `<small>vs</small> ${strings.analytics.authorizations.allAttempts}`,
                        value: allAttempts,
                        valueType: opts.by
                    }];
                }
            },
            reactProps: {
                showBadgeInTabs: false,
                countField: "declines_count",
                otherCountFields: ["auths_count"],
                summaryStyle: 2,
                chart: "pie",
                bodyClass: "pieWithTable"
            }
        }
    });
};
